import {useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import React, {useCallback, useRef} from 'react';
import {type AstView, type EnrichedAstNode} from 'rendition-core';
import {Outline} from 'Views/Preview/RichPreview/Highlighter/Highlighter';
import {HorizontalHandleToggle, VerticalHandleToggle} from './HandleToggle';
import {HorizontalHandle} from './HorizontalAbsoluteHandle';
import {VerticalHandle} from './VerticalAbsoluteHandle';

interface Props {
    node: EnrichedAstNode;
    view: AstView;
    setView: (view: AstView) => void;
}

export const AbsoluteEditHandles: React.FC<Props> = ({node, view, setView}) => {
    const rootNode = view.tree;
    const {nodeCallbacks} = useNodeCallbackContext();

    const ref = useRef<HTMLDivElement | null>(null);

    const getCurrentPreviewLocation = useCallback(() => {
        if (ref.current) {
            const rec = ref.current.getBoundingClientRect();
            return {x: rec.left, y: rec.top, width: rec.width, height: rec.height};
        }
        return {x: 0, y: 0, width: 0, height: 0};
    }, []);

    const locationInfo = nodeCallbacks[node.internalId]()!;
    const rootLocationInfo = nodeCallbacks[rootNode.internalId]()!.actual;

    const isBottom = node.inputLayoutAbstraction.verticalAnchor === 'bottom';
    const isTop = !isBottom;
    const isRight = node.inputLayoutAbstraction.horizontalAnchor === 'right';
    const isLeft = !isRight;

    return (
        <div ref={ref} className="relative">
            <Outline
                borderSize={2}
                border="2px solid #9688EE"
                locationCallBack={nodeCallbacks[node.internalId]}
                rootXY={getCurrentPreviewLocation()}
            ></Outline>
            <VerticalHandle
                xy={getCurrentPreviewLocation}
                locationInfo={nodeCallbacks[node.internalId]}
                rootLocationInfo={nodeCallbacks[rootNode.internalId]}
                itemWidth={locationInfo.actual.width}
                itemHeight={locationInfo.actual.height}
                isTopHandle={isTop}
                relativeParentHeight={rootLocationInfo.height}
                node={node}
                view={view}
                setView={setView}
            />
            <HorizontalHandle
                xy={getCurrentPreviewLocation}
                locationInfo={nodeCallbacks[node.internalId]}
                rootLocationInfo={nodeCallbacks[rootNode.internalId]}
                itemHeight={locationInfo.actual.height}
                itemWidth={locationInfo.actual.width}
                isLeftHandle={isLeft}
                relativeParentWidth={rootLocationInfo.width}
                node={node}
                view={view}
                setView={setView}
            />
            <HorizontalHandleToggle
                onClick={() => {
                    if (node.inputLayoutAbstraction.horizontalAnchor === 'right') {
                        node.inputLayoutAbstraction.horizontalAnchor = 'left';
                    } else {
                        node.inputLayoutAbstraction.horizontalAnchor = 'right';
                    }
                    setView({...view});
                }}
                itemHeight={locationInfo.actual.height}
                edge={isLeft ? 'right' : 'left'}
                xy={getCurrentPreviewLocation}
                locationInfo={nodeCallbacks[node.internalId]}
                rootLocationInfo={nodeCallbacks[rootNode.internalId]}
            />
            <VerticalHandleToggle
                onClick={() => {
                    if (node.inputLayoutAbstraction.verticalAnchor === 'bottom') {
                        node.inputLayoutAbstraction.verticalAnchor = 'top';
                    } else {
                        node.inputLayoutAbstraction.verticalAnchor = 'bottom';
                    }
                    setView({...view});
                }}
                itemWidth={locationInfo.actual.width}
                edge={isTop ? 'bottom' : 'top'}
                xy={getCurrentPreviewLocation}
                locationInfo={nodeCallbacks[node.internalId]}
                rootLocationInfo={nodeCallbacks[rootNode.internalId]}
            />
        </div>
    );
};
